import useServicePriceTotal from '@/hooks/useServicePriceTotal'
import {
    COMMITMENT_PREPAID,
    PRIVATE_DOMAIN_BILLING_CYCLE,
} from '@/modules/Contract/constants'
import { getTimeRegister } from '@/modules/Contract/constants/function'
import {
    Contract,
    ProductResponse,
    ServiceConfigOption,
} from '@/modules/Contract/types'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { Path, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { GridHookForm } from 'vnetwork-ui'
import ServiceTimes from '../../Base/ServiceTimes'

interface SSLConfigProps {
    index: number
    product: ProductResponse
    price: number
    isPOC?: boolean
}

const SSLConfig: React.FC<SSLConfigProps> = ({
    index,
    price,
    isPOC = false,
}) => {
    const formControl = useFormContext<Contract>()
    const { t } = useTranslation()
    const today = dayjs()

    const service_configoptions = useWatch({
        control: formControl.control,
        name: `order.products.[${index}].service_configoptions` as Path<Contract>,
    }) as ServiceConfigOption[]

    const term = useWatch({
        control: formControl?.control,
        name: `order.products.[${index}].term` as Path<Contract>,
    })

    const contract_expiry_date = useWatch({
        control: formControl?.control,
        name: `contract_expiry_date`,
    })

    const contract_effective_date = useWatch({
        control: formControl?.control,
        name: `contract_effective_date`,
    })

    useServicePriceTotal({
        control: formControl.control,
        index,
        service_configoptions,
        setValue: formControl.setValue,
    })

    useEffect(() => {
        formControl.setValue(
            `contract_expiry_date`,
            today.add(getTimeRegister(term) || 1, 'year').subtract(1, 'day')
        )
    }, [term])

    useEffect(() => {
        formControl.setValue(
            `order.products.[${index}].service_times.first_startdate` as Path<Contract>,
            contract_effective_date
        )

        formControl.setValue(
            `order.products.[${index}].service_times.first_enddate` as Path<Contract>,
            contract_expiry_date
        )
    }, [JSON.stringify([contract_expiry_date, contract_effective_date])])

    useEffect(() => {
        formControl.setValue(
            `order.products.[${index}].service_price_register` as Path<Contract>,
            price * (getTimeRegister(term) || 1)
        )
    }, [term])

    return (
        <div data-tour="ssl-config">
            <GridHookForm
                subForm
                actions={[]}
                data={[
                    {
                        formItemProps: {
                            name: `order.products.[${index}].service_domain_metadata.domain_value` as Path<Contract>,
                            type: 'textfield',
                            label: t('ssl_for_domain_name'),
                            placeholder: t('ssl_for_domain_name'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: `order.products.[${index}].term` as Path<Contract>,
                            type: 'select',
                            options: PRIVATE_DOMAIN_BILLING_CYCLE,
                            label: t('domain_registrationperiod'),
                            placeholder: t('domain_registrationperiod'),
                            defaultValue:
                                PRIVATE_DOMAIN_BILLING_CYCLE?.[0]?.value,
                            allowClear: false,
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: `order.products.[${index}].prepaid` as Path<Contract>,
                            type: 'select',
                            // @ts-ignore
                            options: COMMITMENT_PREPAID(t),
                            label: t('commitment_prepaid'),
                            placeholder: t('commitment_prepaid'),
                            allowClear: false,
                            onChangeAfterHookForm: (value) => {
                                const options = service_configoptions?.map(
                                    (option) => ({
                                        ...option,
                                        commitment_prepaid: value,
                                    })
                                )

                                formControl.setValue(
                                    `order.products.[${index}].service_configoptions` as Path<Contract>,
                                    options
                                )
                            },
                        },
                        className: isPOC ? 'hidden' : 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: `order.products.[${index}].service_price_register` as Path<Contract>,
                            type: 'number',
                            label: t('service_price_register'),
                            placeholder: t('service_price_register'),
                        },
                        className: isPOC ? 'hidden' : 'col-span-2',
                    },
                ]}
                formControl={formControl}
            />

            {!isPOC && (
                <ServiceTimes
                    index={index}
                    disabledStatement
                    datesFromContract
                />
            )}
        </div>
    )
}

export default SSLConfig
